/**
 * PaymentsPendingController — page /paiements-en-attente.html (EPIC-042 / 42.4).
 *
 * Liste les transactions « à traiter » (§7.I.F) : paiements détachés au-dessus
 * du seuil légal B2C ou associés à un brouillon de facture. 3 voies par ligne :
 *
 *   1. « Créer la facture » / « Compléter le brouillon » — redirige vers
 *      le wizard facture pré-rempli (cible 42.5+).
 *   2. « Identifier sans facturer » — ouvre un sous-form inline (`<tr class=
 *      "nm-row-subform">` cloné depuis `tpl-pending-subform`), saisit un
 *      `client_name`, PATCH `/v1/payments/{id}` `{clientName}` → la
 *      ligne sort de pending (elle reste détachée mais identifiée).
 *   3. « Ignorer » — POST `/v1/payments/{id}/dismiss` → la ligne sort
 *      de pending de façon persistante (colonne `dismissed_at`, 42.2).
 *
 * Découplage avec le badge nav : chaque sortie de ligne (succès Identifier
 * ou Ignorer) dispatche un `CustomEvent('nm:pending-decremented')` sur
 * `window`, écouté par `app-header-controller` (pas de couplage direct
 * inter-controllers, cf. CLAUDE.md).
 *
 * Pattern `<template>` strict (CLAUDE.md) : la ligne pending et son
 * sous-form Identifier sont deux templates distincts (`tpl-pending-row`,
 * `tpl-pending-subform`), insérés successivement dans `<tbody>`. Le couplage
 * ligne↔sous-form est porté par `data-payment-id` sur les deux fragments.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const SOURCE_LABELS = {
  cash: "Espèces",
  bank_transfer: "Virement",
  check: "Chèque",
  wero: "Wero",
  other: "Autre moyen",
};

export default class PaymentsPendingController extends BaseController {
  static targets = [
    "tableWrap",
    "tableBody",
    "emptyState",
    "banner",
    // EPIC-045 / 45.5 — Section « Demandes de facture (QR) »
    "qrRequestsWrap",
    "qrRequestsTbody",
    "qrRequestsEmpty",
    "qrRequestsActions",
    "qrRequestsLoadMore",
    "qrRequestsCount",
  ];

  // EPIC-045 / 45.5 — pagination « Charger plus » des demandes QR.
  static QR_REQUESTS_PER_PAGE = 25;

  connect() {
    this._fetchSeq = 0;
    this._qrPage = 1;
    this._qrTotal = 0;
    this._qrLoaded = 0;
    this.refresh();
    this.refreshQrRequests();
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    const seq = ++this._fetchSeq;
    if (this.hasTableWrapTarget) this.tableWrapTarget.setAttribute("aria-busy", "true");

    try {
      const items = await api.payments.pending();
      if (seq !== this._fetchSeq) return;
      const list = Array.isArray(items) ? items : [];
      this._renderRows(list);
      this._updateEmptyState(list.length);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      this._renderError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les transactions à traiter.";
      this.showError(msg);
    } finally {
      if (seq === this._fetchSeq && this.hasTableWrapTarget) {
        this.tableWrapTarget.setAttribute("aria-busy", "false");
      }
    }
  }

  _renderRows(items) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();

    for (const item of items) {
      this.tableBodyTarget.appendChild(this._buildRow(item));
      this.tableBodyTarget.appendChild(this._buildSubform(item));
    }
  }

  _buildRow(item) {
    const fragment = this.cloneTemplate("tpl-pending-row");
    const tr = fragment.querySelector("tr");
    if (tr) tr.dataset.paymentId = item.id;

    const sourceLabel = SOURCE_LABELS[item.paymentSource] ?? item.paymentSource ?? "—";

    this.fillSlots(fragment, {
      date: this.formatShortDate(item.transactionDate),
      sourceLabel,
      amount: this._formatAmount(item.amount),
      situationLabel: item.hasDraft ? "Brouillon en cours" : "À facturer",
    });

    // Libellé secondaire « moyen de paiement » (ex. « Mon TPE SumUp »).
    const providerCell = fragment.querySelector('[data-slot-element="providerLabel"]');
    if (providerCell) {
      if (item.providerLabel) {
        providerCell.textContent = item.providerLabel;
      } else {
        providerCell.remove();
      }
    }

    // Bouton primaire : « Créer la facture » ou « Compléter le brouillon ».
    const primary = fragment.querySelector('[data-slot-element="primaryAction"]');
    if (primary) {
      if (item.hasDraft && item.draftInvoiceId) {
        primary.setAttribute(
          "href",
          `/facture-edition.html?id=${encodeURIComponent(item.draftInvoiceId)}`,
        );
        primary.textContent = "Compléter le brouillon";
      } else {
        const qs = new URLSearchParams({
          from: "payment",
          amount: String(item.amount ?? ""),
          date: String(item.transactionDate ?? ""),
        });
        primary.setAttribute("href", `/facture-edition.html?${qs.toString()}`);
        primary.textContent = "Créer la facture";
      }
    }

    // Boutons « Identifier » / « Ignorer » — porter l'id pour les actions.
    fragment.querySelectorAll('[data-slot-attr-data-payment-id]').forEach((el) => {
      el.dataset.paymentId = item.id;
    });

    return fragment;
  }

  _buildSubform(item) {
    const fragment = this.cloneTemplate("tpl-pending-subform");
    const tr = fragment.querySelector("tr");
    if (tr) tr.dataset.paymentId = item.id;
    fragment.querySelectorAll('[data-slot-attr-data-payment-id]').forEach((el) => {
      el.dataset.paymentId = item.id;
    });
    return fragment;
  }

  _renderError() {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-pending-error"));
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = true;
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  _updateEmptyState(count) {
    if (!this.hasEmptyStateTarget) return;
    this.emptyStateTarget.hidden = count > 0;
    if (this.hasTableWrapTarget) this.tableWrapTarget.hidden = count === 0;
  }

  // ---------------------------------------------------------------------------
  // Voie 2 — « Identifier sans facturer » (sous-form inline)
  // ---------------------------------------------------------------------------

  toggleIdentify(event) {
    const id = event.currentTarget.dataset.paymentId;
    if (!id) return;
    const subform = this._findSubform(id);
    if (!subform) return;
    const willOpen = subform.hidden;
    // Refermer tous les autres sous-forms ouverts pour ne pas saturer l'UI.
    this._closeAllSubforms();
    subform.hidden = !willOpen;
    if (willOpen) {
      const input = subform.querySelector("input[type=text]");
      if (input) input.focus();
    }
  }

  cancelIdentify(event) {
    const id = event.currentTarget.dataset.paymentId;
    if (!id) return;
    const subform = this._findSubform(id);
    if (subform) subform.hidden = true;
  }

  async submitIdentify(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const id = form.dataset.paymentId;
    if (!id) return;
    const input = form.querySelector("input[type=text]");
    const clientName = input ? String(input.value || "").trim() : "";
    if (clientName === "") {
      this._showSubformError(id, "Le nom du client est obligatoire.");
      if (input) input.focus();
      return;
    }

    const submitBtn = form.querySelector("button[type=submit]");
    if (submitBtn) submitBtn.disabled = true;

    try {
      await api.payments.update(id, { clientName });
      this._removeRow(id);
      this.showSuccess("Transaction identifiée.");
      this._notifyDecrement();
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible d'identifier la transaction.";
      this._showSubformError(id, msg);
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Voie 3 — « Ignorer » (soft-dismiss persistant)
  // ---------------------------------------------------------------------------

  async dismissPending(event) {
    const btn = event.currentTarget;
    const id = btn.dataset.paymentId;
    if (!id) return;
    btn.disabled = true;
    try {
      await api.payments.dismiss(id);
      this._removeRow(id);
      this.showSuccess("Transaction ignorée.");
      this._notifyDecrement();
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible d'ignorer la transaction.";
      this.showError(msg);
      btn.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers DOM
  // ---------------------------------------------------------------------------

  _findSubform(id) {
    return this.tableBodyTarget.querySelector(
      `tr.nm-row-subform[data-payment-id="${CSS.escape(id)}"]`,
    );
  }

  _showSubformError(id, message) {
    const subform = this._findSubform(id);
    if (!subform) return;
    const alert = subform.querySelector('[data-slot-element="subformError"]');
    if (!alert) return;
    alert.textContent = message;
    alert.hidden = false;
  }

  _closeAllSubforms() {
    this.tableBodyTarget.querySelectorAll("tr.nm-row-subform").forEach((tr) => {
      tr.hidden = true;
      const alert = tr.querySelector('[data-slot-element="subformError"]');
      if (alert) {
        alert.hidden = true;
        alert.textContent = "";
      }
    });
  }

  _removeRow(id) {
    const escaped = CSS.escape(id);
    const rows = this.tableBodyTarget.querySelectorAll(
      `tr[data-payment-id="${escaped}"]`,
    );
    rows.forEach((tr) => tr.remove());
    const remaining = this.tableBodyTarget.querySelectorAll(
      'tr[data-payment-id]:not(.nm-row-subform)',
    ).length;
    this._updateEmptyState(remaining);
  }

  _notifyDecrement() {
    window.dispatchEvent(new CustomEvent("nm:pending-decremented"));
  }

  _formatAmount(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "—";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  // ===========================================================================
  // EPIC-045 / 45.5 — Section « Demandes de facture (QR) »
  // ===========================================================================

  /**
   * Chargement initial (page 1) de la liste des demandes pending. Remplace
   * le contenu actuel du tbody. Pagination par « Charger plus » via
   * {@see loadMoreQrRequests()}.
   */
  async refreshQrRequests() {
    this._qrPage = 1;
    this._qrLoaded = 0;
    if (this.hasQrRequestsWrapTarget) {
      this.qrRequestsWrapTarget.setAttribute("aria-busy", "true");
    }
    try {
      const response = await api.invoiceRequests.list({
        status: "pending",
        page: 1,
        perPage: PaymentsPendingController.QR_REQUESTS_PER_PAGE,
      });
      const items = Array.isArray(response?.data) ? response.data : [];
      this._qrTotal = response?.meta?.total ?? items.length;
      this._clearQrRequestsTbody();
      this._appendQrRequestsRows(items);
      this._updateQrRequestsEmptyState(this._qrLoaded);
      this._updateQrRequestsLoadMore();
    } catch (error) {
      this._renderQrRequestsError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les demandes de facture.";
      this.showError(msg);
    } finally {
      if (this.hasQrRequestsWrapTarget) {
        this.qrRequestsWrapTarget.setAttribute("aria-busy", "false");
      }
    }
  }

  /**
   * Charge la page suivante et ajoute les lignes en bas du tbody (sans
   * effacer les précédentes).
   */
  async loadMoreQrRequests(event) {
    if (event) event.preventDefault();
    if (this.hasQrRequestsLoadMoreTarget) this.qrRequestsLoadMoreTarget.disabled = true;
    try {
      const next = this._qrPage + 1;
      const response = await api.invoiceRequests.list({
        status: "pending",
        page: next,
        perPage: PaymentsPendingController.QR_REQUESTS_PER_PAGE,
      });
      const items = Array.isArray(response?.data) ? response.data : [];
      this._qrPage = next;
      this._appendQrRequestsRows(items);
      this._updateQrRequestsLoadMore();
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger la page suivante.";
      this.showError(msg);
    } finally {
      if (this.hasQrRequestsLoadMoreTarget) {
        this.qrRequestsLoadMoreTarget.disabled = false;
      }
    }
  }

  _appendQrRequestsRows(items) {
    if (!this.hasQrRequestsTbodyTarget) return;
    for (const item of items) {
      this.qrRequestsTbodyTarget.appendChild(this._buildQrRequestRow(item));
      this.qrRequestsTbodyTarget.appendChild(this._buildQrRequestSubform(item));
    }
    this._qrLoaded += items.length;
  }

  _buildQrRequestRow(item) {
    const fragment = this.cloneTemplate("tpl-qr-request-row");
    const tr = fragment.querySelector("tr");
    if (tr) tr.dataset.requestId = item.id;

    this.fillSlots(fragment, {
      submittedAt: this.formatShortDate(item.submittedAt),
      clientName: item.clientName ?? "—",
      clientEmail: item.clientEmail ?? "",
      clientType: (item.clientType || "").toUpperCase() || "—",
      claimedAmount: this._formatAmount(item.claimedAmount),
      claimedDate: this.formatShortDate(item.claimedDate),
    });

    fragment.querySelectorAll('[data-slot-attr-data-request-id]').forEach((el) => {
      el.dataset.requestId = item.id;
    });

    // Lien « Rejeter » → page dédiée demande-facture-refus.html?id=…
    const rejectLink = fragment.querySelector('[data-slot-attr-href]');
    if (rejectLink) {
      rejectLink.setAttribute(
        "href",
        `/demande-facture-refus.html?id=${encodeURIComponent(item.id)}`,
      );
    }

    return fragment;
  }

  _buildQrRequestSubform(item) {
    const fragment = this.cloneTemplate("tpl-qr-request-subform");
    const tr = fragment.querySelector("tr");
    if (tr) tr.dataset.requestId = item.id;
    fragment.querySelectorAll('[data-slot-attr-data-request-id]').forEach((el) => {
      el.dataset.requestId = item.id;
    });
    return fragment;
  }

  _renderQrRequestsError() {
    if (!this.hasQrRequestsTbodyTarget) return;
    this._clearQrRequestsTbody();
    this.qrRequestsTbodyTarget.appendChild(this.cloneTemplate("tpl-qr-request-error"));
    if (this.hasQrRequestsEmptyTarget) this.qrRequestsEmptyTarget.hidden = true;
    if (this.hasQrRequestsActionsTarget) this.qrRequestsActionsTarget.hidden = true;
  }

  _clearQrRequestsTbody() {
    while (this.qrRequestsTbodyTarget.firstChild) {
      this.qrRequestsTbodyTarget.removeChild(this.qrRequestsTbodyTarget.firstChild);
    }
  }

  _updateQrRequestsEmptyState(count) {
    if (this.hasQrRequestsEmptyTarget) this.qrRequestsEmptyTarget.hidden = count > 0;
    if (this.hasQrRequestsWrapTarget) this.qrRequestsWrapTarget.hidden = count === 0;
  }

  _updateQrRequestsLoadMore() {
    if (!this.hasQrRequestsActionsTarget) return;
    const hasMore = this._qrLoaded < this._qrTotal;
    this.qrRequestsActionsTarget.hidden = !hasMore && this._qrTotal === 0;
    if (this.hasQrRequestsLoadMoreTarget) {
      this.qrRequestsLoadMoreTarget.hidden = !hasMore;
    }
    if (this.hasQrRequestsCountTarget) {
      this.qrRequestsCountTarget.textContent = this._qrTotal > 0
        ? `${this._qrLoaded} demande(s) affichée(s) sur ${this._qrTotal}`
        : "";
    }
  }

  // ---------------------------------------------------------------------------
  // Action « Rapprocher » — sous-form inline
  // ---------------------------------------------------------------------------

  async toggleQrRequestRapprocher(event) {
    const id = event.currentTarget.dataset.requestId;
    if (!id) return;
    const subform = this._findQrSubform(id);
    if (!subform) return;
    const willOpen = subform.hidden;
    this._closeAllQrSubforms();
    subform.hidden = !willOpen;
    if (willOpen) {
      // Charger la liste des transactions SumUp détachées au moment de
      // l'ouverture (lazy — évite N appels au chargement initial).
      await this._loadDetachedSumupTransactions(subform);
    }
  }

  cancelQrRequestRapprocher(event) {
    const id = event.currentTarget.dataset.requestId;
    if (!id) return;
    const subform = this._findQrSubform(id);
    if (subform) subform.hidden = true;
  }

  async submitQrRequestRapprocher(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const id = form.dataset.requestId;
    if (!id) return;
    const select = form.querySelector('[data-slot-element="transactionSelect"]');
    const paymentTransactionId = select ? String(select.value || "").trim() : "";
    if (paymentTransactionId === "") {
      this._showQrSubformError(id, "Sélectionnez une transaction.");
      return;
    }
    const submitBtn = form.querySelector("button[type=submit]");
    if (submitBtn) submitBtn.disabled = true;
    try {
      await api.invoiceRequests.link(id, paymentTransactionId);
      this._removeQrRow(id);
      this.showSuccess("Demande rapprochée.");
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de rapprocher la demande.";
      this._showQrSubformError(id, msg);
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  /**
   * Charge la liste des transactions SumUp détachées disponibles dans le
   * `<select>` du sous-form. La liste exclut les transactions déjà liées
   * à une facture (filtre `detached=1`).
   */
  async _loadDetachedSumupTransactions(subform) {
    const select = subform.querySelector('[data-slot-element="transactionSelect"]');
    if (!select) return;
    select.disabled = true;
    try {
      // §7.K — SumUp est représenté par `paymentSource=other` + un
      // payment_provider dont `code=sumup` (cf. PaymentSource enum).
      const response = await api.payments.list({
        source: "other",
        detached: 1,
        perPage: 50,
      });
      const items = Array.isArray(response?.data) ? response.data : [];
      // Vider et reconstruire le select avec les transactions.
      while (select.firstChild) select.removeChild(select.firstChild);
      const blank = document.createElement("option");
      blank.value = "";
      blank.textContent = items.length > 0
        ? "— Choisir une transaction —"
        : "— Aucune transaction détachée —";
      select.appendChild(blank);
      for (const tx of items) {
        const opt = document.createElement("option");
        opt.value = tx.id;
        opt.textContent = `${this.formatShortDate(tx.transactionDate)} — ${this._formatAmount(tx.amount)}${tx.externalId ? ` — #${tx.externalId}` : ""}`;
        select.appendChild(opt);
      }
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les transactions.";
      this._showQrSubformErrorOnForm(subform, msg);
    } finally {
      select.disabled = false;
    }
  }

  _findQrSubform(id) {
    return this.qrRequestsTbodyTarget.querySelector(
      `tr.nm-row-subform[data-request-id="${CSS.escape(id)}"]`,
    );
  }

  _closeAllQrSubforms() {
    this.qrRequestsTbodyTarget.querySelectorAll("tr.nm-row-subform").forEach((tr) => {
      tr.hidden = true;
      const alert = tr.querySelector('[data-slot-element="subformError"]');
      if (alert) {
        alert.hidden = true;
        alert.textContent = "";
      }
    });
  }

  _showQrSubformError(id, message) {
    const subform = this._findQrSubform(id);
    if (subform) this._showQrSubformErrorOnForm(subform, message);
  }

  _showQrSubformErrorOnForm(subform, message) {
    const alert = subform.querySelector('[data-slot-element="subformError"]');
    if (!alert) return;
    alert.textContent = message;
    alert.hidden = false;
  }

  _removeQrRow(id) {
    const escaped = CSS.escape(id);
    const rows = this.qrRequestsTbodyTarget.querySelectorAll(
      `tr[data-request-id="${escaped}"]`,
    );
    rows.forEach((tr) => tr.remove());
    this._qrLoaded = Math.max(0, this._qrLoaded - 1);
    this._qrTotal = Math.max(0, this._qrTotal - 1);
    this._updateQrRequestsEmptyState(this._qrLoaded);
    this._updateQrRequestsLoadMore();
  }
}
